/**
 * Authentication Context Provider
 *
 * This file provides authentication state and methods to the entire application.
 * It wraps the app with AuthProvider and exposes user session data via context.
 *
 * @fileoverview React Context for Supabase authentication state management
 *
 * @example
 * // Wrap your app with AuthProvider
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 *
 * // Access auth state in components
 * const { user, signIn, signOut } = useAuth();
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { apiFetch, apiJson, setMfaRequiredHandler, toUserMessage } from '../lib/api';
import { isE2EAuthBypassActive, buildE2EBypassSession } from '../lib/e2eAuth';

/**
 * Authentication context state interface
 */
export interface AuthContextState {
  /** Current authenticated user, null if not logged in */
  user: User | null;
  /** Current session, null if not authenticated */
  session: Session | null;
  /** Loading state during auth operations */
  loading: boolean;
  /** Error message from last auth operation */
  error: string | null;
  /** Whether MFA verification is required */
  mfaRequired: boolean;
  /** Whether MFA check is currently in progress */
  checkingMfa: boolean;
  /** Whether MFA has been verified for current session */
  mfaVerified: boolean;
  /**
   * Whether the MFA determination for the CURRENT session has landed at
   * least once. `mfaRequired` alone can't answer "is it safe to trust
   * mfaRequired yet?" — it starts `false` before the challenge check has
   * even run, which is indistinguishable from "checked and not required."
   * Consumers that must never call an MfaGuard-protected endpoint ahead of
   * that determination (see UserProvider) gate on this instead.
   */
  mfaChecked: boolean;
}



/**
 * Authentication context methods interface
 */
export interface AuthContextMethods {
  /**
   * Sign in with email and password
   * @param email - User's email
   * @param password - User's password
   * @returns Promise resolving to success status
   */
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;

  /**
   * Sign up with email, password, and optional user metadata
   * @param email - User's email
   * @param password - User's password
   * @param metadata - Optional user metadata (displayName, firstName, lastName, phoneNumber)
   * @returns Promise resolving to success status and message
   */
  signUp: (
    email: string,
    password: string,
    metadata?: {
      displayName?: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
    }
  ) => Promise<{ success: boolean; error?: string; message?: string }>;

  /**
   * Sign out the current user
   * @returns Promise resolving when sign out is complete
   */
  signOut: () => Promise<void>;

  /**
   * Send password reset email
   * @param email - User's email
   * @returns Promise resolving to success status
   */
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;

  /**
   * Update password (when user has reset token)
   * @param newPassword - New password
   * @returns Promise resolving to success status
   */
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;

  /**
   * Clear any error messages
   */
  clearError: () => void;

  /**
   * Verify MFA code
   * @param code - The 6-digit verification code
   */
  verifyMfa: (code: string) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Complete auth context type combining state and methods
 */
export type AuthContextType = AuthContextState & AuthContextMethods;

/**
 * Default context value for initialization
 */
const defaultAuthContext: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  error: null,
  mfaRequired: false,
  checkingMfa: false,
  mfaVerified: false,
  mfaChecked: false,
  signIn: async () => ({ success: false }),

  signUp: async () => ({ success: false }),
  signOut: async () => {},
  resetPassword: async () => ({ success: false }),
  updatePassword: async () => ({ success: false }),
  clearError: () => {},
  verifyMfa: async () => ({ success: false }),
};

/**
 * Auth Context instance
 */
const AuthContext = createContext<AuthContextType>(defaultAuthContext);

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
  /** Child components to wrap */
  children: ReactNode;
}

/**
 * How long to wait on POST /api/auth/mfa/challenge before giving up.
 * Must stay comfortably above the backend's own 5s cap on the outbound
 * Resend call (see MfaService.generateChallenge) — at the previous 5s the
 * client aborted a request the server was about to answer successfully,
 * so the code reached the user's inbox while the UI reported a failure.
 */
const CHALLENGE_TIMEOUT_MS = 15000;

/**
 * Authentication Provider Component
 *
 * Wraps the application and provides authentication state and methods
 * to all child components via React Context.
 *
 * Features:
 * - Automatic session persistence
 * - Real-time auth state synchronization
 * - Handles OAuth and email confirmation redirects
 *
 * @param props - Component props
 * @returns Provider component wrapping children
 *
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [checkingMfa, setCheckingMfa] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [mfaChecked, setMfaChecked] = useState(false);
  
  // Track if MFA was already checked this session to avoid duplicate checks
  const mfaCheckedRef = useRef(false);

  /**
   * Register the global MFA-required handler so ANY api.ts call — not just
   * the post-login challenge check — can flip mfaRequired when the backend's
   * MfaGuard 403s with MFA_REQUIRED (e.g. the per-user verification TTL
   * lapsed mid-session). Without this, the backend enforcement added to
   * MfaGuard would just show as an opaque failed request instead of
   * routing the user back to /mfa-verify.
   */
  useEffect(() => {
    setMfaRequiredHandler(() => {
      setMfaRequired(true);
      setMfaVerified(false);
    });
    return () => setMfaRequiredHandler(null);
  }, []);

  /**
   * Ask the backend whether this session still owes MFA, letting it email a
   * code if so. The userId is deliberately not sent — the backend derives it
   * from the verified JWT and ignores anything in the body.
   */
  const checkMfa = useCallback(async (token: string): Promise<boolean> => {
    setCheckingMfa(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHALLENGE_TIMEOUT_MS);

    try {
      const data = await apiJson<{ required: boolean }>(
        '/api/auth/mfa/challenge',
        { method: 'POST', signal: controller.signal },
        token
      );

      setMfaRequired(data.required);
      setMfaVerified(!data.required);
      return data.required;
    } catch (err) {
      // Fail CLOSED. This used to fall through to mfaVerified=true, so any
      // timeout, 429 or network blip let the router render the dashboard for
      // a session the backend considers unverified — and every subsequent
      // call then 403'd with MFA_REQUIRED. The backend is the authority; if
      // we can't reach it, assume MFA is still owed.
      console.error('[AuthContext] MFA check failed, assuming MFA required:', err);
      setMfaRequired(true);
      setMfaVerified(false);
      return true;
    } finally {
      clearTimeout(timeoutId);
      setCheckingMfa(false);
      // Marks the determination as final for consumers gating on it (e.g.
      // UserProvider), regardless of which branch above ran.
      setMfaChecked(true);
    }
  }, []);

  /**
   * Check MFA only if not already checked for this session
   * Uses ref to prevent stale closures and unnecessary re-execution
   */
  const checkMfaIfNeeded = useCallback(async (token: string) => {
    // Skip if already checked for this session
    if (mfaCheckedRef.current) {
      return;
    }

    mfaCheckedRef.current = true;
    await checkMfa(token);
  }, []);

  /**
   * Format auth errors for user display
   */
  const formatError = (error: AuthError | Error | null): string => {
    if (!error) return 'An unknown error occurred';

    // Map common Supabase errors to user-friendly messages
    const message = error.message.toLowerCase();

    if (message.includes('invalid login credentials')) {
      return 'Invalid email or password';
    }
    if (message.includes('email not confirmed')) {
      return 'Please confirm your email before signing in';
    }
    if (message.includes('user already registered')) {
      return 'An account with this email already exists';
    }
    if (message.includes('password')) {
      return 'Password does not meet requirements';
    }

    return error.message;
  };

  /**
   * Initialize auth state and listen for changes
   */
  useEffect(() => {
    // Prevent double initialization in React StrictMode
    let isSubscribed = true;

    // E2E bypass: skip Supabase entirely and inject a fabricated session so
    // ProtectedRoute treats this as an authenticated, MFA-cleared user.
    // See lib/e2eAuth.ts for the double gate that keeps this out of
    // production builds.
    if (isE2EAuthBypassActive()) {
      const { user: bypassUser, session: bypassSession } =
        buildE2EBypassSession();
      setUser(bypassUser);
      setSession(bypassSession);
      // checkMfa() never runs on this path, so mfaChecked would otherwise
      // stay false forever — and UserProvider now refuses to fetch tenants
      // until mfaChecked is true, which would hang every e2e test.
      setMfaRequired(false);
      setMfaVerified(true);
      setMfaChecked(true);
      setLoading(false);
      return () => {
        isSubscribed = false;
      };
    }

    // Get initial session with timeout to handle Firefox hanging issue
    const initializeAuth = async () => {

      try {


        // Add timeout to prevent infinite hanging (Firefox issue)
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Session fetch timeout')), 5000);
        });

        const sessionPromise = supabase.auth.getSession();

        const { data: { session: initialSession } } = await Promise.race([
          sessionPromise,
          timeoutPromise,
        ]) as Awaited<typeof sessionPromise>;


        if (isSubscribed) {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
        }

        // A restored session (page reload, new tab, returning visitor) is
        // authenticated but says nothing about MFA. Without this check the
        // router rendered the dashboard with mfaRequired=false, every API
        // call came back 403 MFA_REQUIRED from the backend guard, and the
        // user was bounced to /mfa-verify without ever having been sent a
        // code — because only signIn/signUp used to call /challenge.
        // The challenge endpoint is idempotent for an already-verified user
        // (returns required:false and sends no email), so running it on
        // every restore is safe.
        if (
          isSubscribed &&
          initialSession?.access_token &&
          initialSession.user?.id
        ) {
          await checkMfaIfNeeded(initialSession.access_token);
        }
      } catch (err) {
        console.error('[AuthContext] Error getting session:', err);
        // On timeout or error, assume no session
        if (isSubscribed) {
          setSession(null);
          setUser(null);
        }
      } finally {

        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth state changes (only update state, don't do side effects)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {

        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Clear errors on successful auth events
        if (event === 'SIGNED_IN') {
          setError(null);
        }
        
        if (event === 'SIGNED_OUT') {
            // Clear local storage when signed out (manual or expired)
            localStorage.removeItem('xacto_support_mode');
            localStorage.removeItem('xacto_signup_tenant_id');
            localStorage.removeItem('link_token');
            localStorage.removeItem('oauth_state_id');

            mfaCheckedRef.current = false;
            setMfaRequired(false);
            setMfaVerified(false);
            setMfaChecked(false);
        }
        
        // Ensure loading is false if we are just updating content (but initializeAuth handles the main initial load)
        if (event !== 'INITIAL_SESSION') { 
             setLoading(false); 
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
    };
  }, []);



  /**
   * Sign in with email and password
   */
  const signIn = useCallback(
    async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {

      // Reset MFA check flag so MFA is always re-checked on each login
      mfaCheckedRef.current = false;
      setMfaChecked(false);

      setLoading(true);
      setError(null);

      try {


        // Add timeout for Firefox hanging issue
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Sign in timeout')), 10000);
        });

        const signInPromise = supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

        const { data, error: signInError } = await Promise.race([
          signInPromise,
          timeoutPromise,
        ]) as Awaited<typeof signInPromise>;



        if (signInError) {
          const errorMessage = formatError(signInError);
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }


        
        if (data.session?.access_token && data.session?.user?.id) {
            await checkMfaIfNeeded(data.session.access_token);
        }


        setUser(data.user);
        setSession(data.session);
        return { success: true };
      } catch (err) {
        console.error('[AuthContext] signIn error:', err);
        const errorMessage = formatError(err as Error);
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {

        setLoading(false);
      }
    },
    []
  );

  /**
   * Sign up with email and password
   */
  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: {
        displayName?: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
      }
    ): Promise<{ success: boolean; error?: string; message?: string }> => {
      setLoading(true);
      setError(null);

      try {
        // Build full name from firstName and lastName if provided
        const fullName = metadata?.firstName && metadata?.lastName
          ? `${metadata.firstName} ${metadata.lastName}`
          : metadata?.displayName?.trim() || '';

        const { data, error: signUpError } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              display_name: metadata?.displayName?.trim() || fullName,
              full_name: fullName,
              first_name: metadata?.firstName?.trim() || '',
              last_name: metadata?.lastName?.trim() || '',
              phone_number: metadata?.phoneNumber?.trim() || '',
            },
          },
        });

        if (signUpError) {
          const errorMessage = formatError(signUpError);
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }

        // Check if email confirmation is required
        if (data.user && !data.session) {
          return {
            success: true,
            message: 'Please check your email to confirm your account',
          };
        }

        // If session exists, user is logged in immediately
        if (data.session) {
          setUser(data.user);
          setSession(data.session);
          if (data.session.access_token && data.session.user?.id) {
              await checkMfaIfNeeded(data.session.access_token);
          }
          return { success: true };
        }

        return {
          success: true,
          message: 'Please check your email to confirm your account',
        };
      } catch (err) {
        const errorMessage = formatError(err as Error);
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        const msg = signOutError.message || '';
        if (msg.toLowerCase().includes('forbidden') || msg.includes('403')) {
          console.warn('[AuthContext] Ignoring signOut 403/forbidden response');
        } else {
          throw signOutError;
        }
      }
      setUser(null);
      setSession(null);
    } catch (err) {
      console.error('Error signing out:', err);
      // Suppress sign-out errors to avoid blocking logout UX
      setError(null);
    } finally {
      // Clear all local storage items to ensure clean state for next login
      localStorage.removeItem('xacto_support_mode');
      localStorage.removeItem('xacto_signup_tenant_id');
      localStorage.removeItem('link_token');
      localStorage.removeItem('oauth_state_id');
      
      setLoading(false);
      setMfaRequired(false); // Reset MFA state on logout
    }
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(
    async (email: string): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);
      setError(null);

      try {
        // If email is provided, use the public endpoint (for password recovery)
        // Otherwise, use the authenticated endpoint (for profile password reset)
        if (email) {
          // Public endpoint for unauthenticated password recovery
          await apiJson('/api/auth/password-reset-by-email', {
            method: 'POST',
            body: JSON.stringify({ email }),
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } else {
          // Authenticated endpoint for users resetting password from profile
          await apiJson('/api/auth/password-reset', {
            method: 'POST',
          }, session?.access_token);
        }

        return { success: true };
      } catch (err) {
        const errorMessage = formatError(err as Error);
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [session?.access_token]
  );

  /**
   * Update password (after reset)
   */
  const updatePassword = useCallback(
    async (newPassword: string): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);
      setError(null);

      try {
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (updateError) {
          const errorMessage = formatError(updateError);
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }

        return { success: true };
      } catch (err) {
        const errorMessage = formatError(err as Error);
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Verify MFA code
   */
  const verifyMfa = useCallback(
    async (code: string): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);
      setError(null);

      try {
        if (!session?.access_token) {
          throw new Error('Your session has expired. Please sign in again.');
        }

        // No userId in the body: the backend takes it from the verified JWT
        // and ignores the body, so sending one only implies otherwise.
        await apiJson<{ success: boolean }>(
          '/api/auth/mfa/verify',
          { method: 'POST', body: JSON.stringify({ code }) },
          session.access_token
        );

        setMfaRequired(false);
        setMfaVerified(true);

        return { success: true };
      } catch (err) {
        const errorMessage = toUserMessage(
          err, "That code didn't work. Please check it and try again.");
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [session]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      loading,
      error,
      mfaRequired,
      checkingMfa,
      mfaVerified,
      mfaChecked,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      clearError,
      verifyMfa,
    }),
    [user, session, loading, error, mfaRequired, checkingMfa, mfaVerified, mfaChecked, signIn, signUp, signOut, resetPassword, updatePassword, clearError, verifyMfa]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication context
 *
 * Must be used within an AuthProvider component.
 *
 * @returns Authentication context with user state and methods
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * const { user, signIn, signOut, loading, error } = useAuthContext();
 *
 * if (loading) return <Spinner />;
 * if (!user) return <LoginForm />;
 * return <Dashboard user={user} />;
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
